import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp, faPencil, faRotateRight } from '@fortawesome/free-solid-svg-icons';
import QRCode from 'react-qr-code';
import LazyImage from '../LazyImage';

function isPlaceholderTitle(value) {
  const normalized = (value || '').toString().trim().toLowerCase();
  if (!normalized) return true;
  if (normalized === 'next step') return true;
  if (/^step\s+\d+$/.test(normalized)) return true;
  return false;
}

function GuideStepAiModal({
  show,
  onHide,
  step,
  connectionLoading,
  connectionError,
  connection,
  connectionNotice,
  available,
  settingsUnavailable,
  onRefreshConnection,
  settingsConnectionsHref,
  titleValue,
  onTitleChange,
  promptHintValue,
  onPromptHintChange,
  modelOptions,
  selectedModel,
  onModelChange,
  contextStepOptions,
  previousStepsContextCount,
  onPreviousStepsContextCountChange,
  followingStepsContextCount,
  onFollowingStepsContextCountChange,
  onUploadFiles,
  showRemoteUploader,
  onToggleRemoteUploader,
  uploaderUrl,
  connectedClients,
  onCopyUploaderUrl,
  onOpenImageMarkup,
  generationLoading,
  generationError,
  generatedText,
  onGenerate,
  onAccept
}) {
  const uploadInputRef = useRef(null);
  const imageEditorRef = useRef(null);
  const [imageEditing, setImageEditing] = useState(false);

  useEffect(() => {
    if (!show && imageEditorRef.current) {
      const editor = imageEditorRef.current;
      imageEditorRef.current = null;
      editor.close();
      setImageEditing(false);
    }
  }, [show]);
  useEffect(() => () => {
    const editor = imageEditorRef.current;
    imageEditorRef.current = null;
    editor?.close();
  }, []);
  useEffect(() => {
    if (!imageEditing) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape' && imageEditorRef.current) {
        // Escape belongs to the topmost editor, not the modal behind it.
        event.preventDefault();
        event.stopImmediatePropagation();
        imageEditorRef.current.close();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [imageEditing]);

  function openImageEditor(event) {
    const trigger = event.currentTarget;
    const editor = onOpenImageMarkup(trigger.querySelector('img'));
    if (!editor) return;
    imageEditorRef.current = editor;
    setImageEditing(true);
    editor.addEventListener('close', () => {
      if (imageEditorRef.current !== editor) return;
      imageEditorRef.current = null;
      setImageEditing(false);
      if (trigger.isConnected) trigger.focus();
    });
  }

  const eligibleImages = useMemo(() => {
    const images = Array.isArray(step?.images) ? step.images : [];
    return images.filter((image) => {
      const type = (image?.type || '').toString().trim().toLowerCase();
      return type !== 'video' && !!image?.url;
    });
  }, [step?.images]);

  const titleMissing = isPlaceholderTitle(titleValue);
  const canGenerate = !connectionLoading
    && available && !settingsUnavailable && !connectionError
    && modelOptions?.some((model) => model.id === selectedModel)
    && !generationLoading
    && !titleMissing
    && eligibleImages.length > 0;

  function handleUploadButtonClick() {
    if (uploadInputRef.current) {
      uploadInputRef.current.click();
    }
  }

  function handleUploadInputChange(event) {
    const fileList = event?.target?.files;
    if (typeof onUploadFiles === 'function' && fileList?.length) {
      onUploadFiles(fileList);
    }
    if (event?.target) {
      event.target.value = '';
    }
  }

  return (
    <Modal
      show={show}
      onHide={generationLoading || imageEditing ? undefined : onHide}
      keyboard={!imageEditing}
      enforceFocus={!imageEditing}
      centered
      size="xl"
      scrollable
      animation={false}
      aria-labelledby="guide-step-ai-title"
    >
      <Modal.Header closeButton={!generationLoading}>
        <Modal.Title id="guide-step-ai-title">AI-assisted step text generation</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="mb-3 text-muted">
          Generate concise step instructions from the current step title, images, project context, relevant BOMs, and the server-managed example guides.
        </div>

        <input
          ref={uploadInputRef}
          type="file"
          accept="image/*"
          capture
          multiple
          style={{ display: 'none' }}
          onChange={handleUploadInputChange}
        />

        {connectionLoading ? (
          <div className="mb-3 d-flex align-items-center gap-2">
            <Spinner animation="border" size="sm" />
            <span role="status">Checking your AI connection...</span>
          </div>
        ) : null}

        {connectionError ? (
          <Alert variant="danger" className="mb-3">
            {connectionError}
          </Alert>
        ) : null}

        {settingsUnavailable ? (
          <Alert variant="warning" className="mb-3">
            Your saved connections are temporarily unavailable. Refresh to try again; your draft is preserved.
          </Alert>
        ) : null}

        {connectionNotice ? <Alert variant="info">{connectionNotice}</Alert> : null}

        <Alert variant={available ? 'light' : 'warning'} className="mb-3 py-2 px-3 small">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-1">
            <div className="text-break">
              {connection ? <><strong>{connection.name}</strong> · {connection.provider === 'openai' ? 'OpenAI' : 'DeepSeek'}</> : 'Select an active connection to generate.'}
            </div>
            <div className="d-flex align-items-center gap-3">
              <a
                href={settingsConnectionsHref}
                target="_blank"
                rel="noopener noreferrer"
                className="py-1"
              >
                Connections
              </a>
              <Button
                type="button"
                variant="link"
                size="sm"
                className="px-0 py-1"
                onClick={onRefreshConnection}
                disabled={connectionLoading}
              >
                <FontAwesomeIcon icon={faRotateRight} className="me-1" />
                Refresh
              </Button>
            </div>
          </div>
          {connection ? <p className="mb-0">Text, images and context are sent to {connection.provider === 'openai' ? 'OpenAI' : 'DeepSeek'} using this API account. Charges may apply.</p> : null}
        </Alert>

        <div className="row g-4">
          <div className="col-12 col-lg-6">
            <Form.Group className="mb-3" controlId="guide-ai-step-title">
              <Form.Label>Step title</Form.Label>
              <Form.Control
                type="text"
                value={titleValue}
                onChange={(event) => onTitleChange(event.target.value)}
                maxLength={200}
                placeholder="Add a descriptive step title"
                disabled={generationLoading}
              />
              {titleMissing ? (
                <Form.Text className="text-danger">
                  A descriptive title is required. Placeholder titles like &quot;Next step&quot; are not enough context.
                </Form.Text>
              ) : (
                <Form.Text className="text-muted">
                  The title is sent to the model and, if you accept the result, it is also applied to the step.
                </Form.Text>
              )}
            </Form.Group>

            <>
                <Form.Group className="mb-3" controlId="guide-ai-guidance">
                  <Form.Label>Optional guidance for the generated step</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={promptHintValue}
                    onChange={(event) => onPromptHintChange(event.target.value)}
                    maxLength={12000}
                    placeholder="Describe roughly what this step should say. Existing step text can be used as a starting point."
                    disabled={generationLoading}
                  />
                  <Form.Text className="text-muted">
                    This text guides generation only. It does not replace the step until you accept the generated result.
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3" controlId="guide-ai-model">
                  <Form.Label>Model</Form.Label>
                  <Form.Select
                    value={selectedModel}
                    onChange={(event) => onModelChange(event.target.value)}
                    disabled={generationLoading || connectionLoading || !available}
                  >
                    {(Array.isArray(modelOptions) ? modelOptions : []).map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    {((Array.isArray(modelOptions) ? modelOptions : []).find((option) => option.id === selectedModel)?.description) || ''}
                  </Form.Text>
                </Form.Group>

                <div className="guide-step-ai-context-grid mb-3">
                  <Form.Group controlId="guide-ai-previous-context">
                    <Form.Label>Include context from previous steps</Form.Label>
                    <Form.Select
                      value={String(previousStepsContextCount)}
                      onChange={(event) => onPreviousStepsContextCountChange(event.target.value)}
                      disabled={generationLoading}
                    >
                      {(Array.isArray(contextStepOptions) ? contextStepOptions : []).map((option) => (
                        <option key={`previous-${option.value}`} value={String(option.value)}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Use this only if the previous steps are well documented. The selected previous step titles and texts will be sent without images.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group controlId="guide-ai-following-context">
                    <Form.Label>Include context from following steps</Form.Label>
                    <Form.Select
                      value={String(followingStepsContextCount)}
                      onChange={(event) => onFollowingStepsContextCountChange(event.target.value)}
                      disabled={generationLoading}
                    >
                      {(Array.isArray(contextStepOptions) ? contextStepOptions : []).map((option) => (
                        <option key={`following-${option.value}`} value={String(option.value)}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      If enabled, only the titles of the selected following steps will be sent.
                    </Form.Text>
                  </Form.Group>
                </div>
            </>

            {generationError ? (
              <Alert variant="danger" className="mb-3">
                {generationError}
              </Alert>
            ) : null}

          </div>

          <div className="col-12 col-lg-6">
            <div className="mb-2 fw-semibold">Step images</div>
            <div className="text-muted mb-3">
              The model works best when the images are annotated with colored arrows or notes. Click a thumbnail to open the annotation editor.
            </div>

            {eligibleImages.length ? (
              <div className="guide-step-ai-thumbnail-grid mb-3">
                {eligibleImages.map((image, index) => (
                  <button
                    key={`${image.url}-${index}`}
                    type="button"
                    className="guide-step-ai-thumbnail-button"
                    title="Open annotation editor"
                    onClick={openImageEditor}
                    disabled={generationLoading}
                  >
                    <LazyImage
                      src={image.url}
                      alt={`Step image ${index + 1}`}
                      className="guide-step-ai-thumbnail-image"
                    />
                    <span className="guide-step-ai-thumbnail-overlay">
                      <FontAwesomeIcon icon={faPencil} />
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <Alert variant="warning" className="mb-3">
                Add at least one non-video image to this step before generating text.
              </Alert>
            )}

            <div className="d-flex flex-wrap gap-2 mb-3">
              <Button
                type="button"
                variant="outline-dark"
                onClick={handleUploadButtonClick}
                disabled={generationLoading}
              >
                Upload image
              </Button>
              <Button
                type="button"
                variant="outline-dark"
                onClick={onToggleRemoteUploader}
                disabled={generationLoading}
              >
                <FontAwesomeIcon icon={faCloudArrowUp} className="me-2" />
                Upload from another device ({connectedClients})
              </Button>
            </div>

            {showRemoteUploader ? (
              <div className="guide-step-ai-qr-card">
                <div className="mb-3" style={{ textAlign: 'center' }}>
                  <QRCode
                    size={256}
                    style={{ height: 'auto', maxWidth: '100%', width: '48%' }}
                    value={uploaderUrl}
                    viewBox="0 0 256 256"
                  />
                </div>
                <div className="mb-2">
                  Scan this QR code to open the remote uploader on another device. The remote uploader will target the currently selected step.
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-dark"
                  onClick={onCopyUploaderUrl}
                >
                  Copy uploader URL
                </button>
              </div>
            ) : null}

            {generatedText ? (
              <Form.Group className="mt-4" controlId="guide-ai-result">
                <Form.Label>Generated step text</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={12}
                  readOnly
                  value={generatedText}
                  className="guide-step-ai-result-box"
                />
              </Form.Group>
            ) : null}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onHide}
          disabled={generationLoading}
        >
          Close
        </Button>
        {generatedText ? (
          <Button
            type="button"
            variant="dark"
            onClick={onAccept}
            disabled={generationLoading || connectionLoading || !available}
          >
            Use generated text
          </Button>
        ) : null}
        <Button
          type="button"
          variant="primary"
          onClick={onGenerate}
          disabled={!canGenerate}
        >
          {generationLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Generating...
            </>
          ) : (
            'Generate step text'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default GuideStepAiModal;
